<script lang="ts" setup>
import LottieAnimation from '@/components/common/LottieAnimation.vue';
import type { ComputedRef, PropType} from 'vue';
import { computed, ref, watch } from 'vue';

const props = defineProps({
    icon: {
        type: String,
        default: ''
    },
    size: {
        type: String as PropType<'xsmall' | 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge' | 'xxxlarge'>,
        default: 'medium'
    },
    trigger: {
        type: String,
        default: ''
    },
    autoPlay: {
        type: Boolean,
        default: false
    },
    loop: {
        type: Boolean,
        default: false
    },
    toggled: {
        type: Boolean,
        default: false
    },
    speed: {
        type: Number,
        default: 0.75
    },
    startFrame: {
        type: Number,
        default: null
    },
    onClick: {
        type: Function,
        default: null
    },
});

const emits = defineEmits(['animationComplete']);

const playOn = ref('');
const stopOn = ref('');
const clickEvent: ComputedRef<(payload: MouseEvent) => void> = computed(() => (props.onClick ?? ((): any => {})) as ((payload: MouseEvent) => void));
const animationController = ref<any>(null);

const onToggledChange = (): void => {
    if (animationController.value == null) return;
    if (props.toggled) {
        animationController.value.setDirection(1);
        animationController.value.play();
    } else {
        animationController.value.setDirection(-1);
        animationController.value.play();
    }
};

const setAnimController = (controller: any): void => {
    animationController.value = controller;
    // Sets the toggle state as soon as the controller is set.
    onToggledChange();
};

const iconSize = computed((): number => {
    switch (props.size) {
        case 'xsmall':
            return 12;
        case 'small':
            return 16;
        case 'medium':
            return 20;
        case 'large':
            return 24;
        case 'xlarge':
            return 32;
        case 'xxlarge':
            return 48;
        case 'xxxlarge':
            return 96;
        default:
            return 0;
    }
});

const play = (): void => {
    if (props.autoPlay) return;
    if (animationController.value == null) return;
    if (props.startFrame != null) {
        animationController.value.setDirection(1);
        animationController.value.goToAndPlay(props.startFrame, true);
    } else {
        animationController.value.setDirection(1);
        animationController.value.play();
    }
};

const stop = (): void => {
    if (props.autoPlay) return;
    animationController.value.setDirection(-1);
    animationController.value.play();
};

watch(() => props.toggled, () => {
    onToggledChange();
});

switch (props.trigger) {
    case 'hover':
        playOn.value = 'mouseenter';
        stopOn.value = 'mouseleave';
        break;
    case 'click':
        playOn.value = 'click';
        break;
    default:
        break;
}
</script>

<template>
    <div
        class="flex-container align-middle"
        @[playOn]="play"
        @[stopOn]="stop"
        @click="clickEvent"
    >
        <LottieAnimation
            class="animation-wrapper"
            :icon="icon"
            :loop="loop"
            :auto-play="autoPlay"
            :speed="speed"
            :width="iconSize"
            :height="iconSize"
            @anim-control="setAnimController"
            @on-complete="emits('animationComplete')"
        />
    </div>
</template>

<style scoped lang="scss">
.animation-wrapper {
    margin: 0;
}
</style>
